import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, MenuItem, Autocomplete,
    Alert, CircularProgress, Snackbar, useTheme, useMediaQuery,
    Tabs, Tab, IconButton, Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VideocamIcon from '@mui/icons-material/Videocam';
import api from '../services/api';
import BuscaPaciente from './BuscaPaciente';
import { confirmAcao } from '../services/alerts';
import { consumirAutorizacao } from '../services/autorizacaoOperacao';
import { isVisualizador } from '../services/auth';

const STATUS_OPCOES = [
    { v: 'AGENDADA', l: 'Agendada' },
    { v: 'REALIZADA', l: 'Realizada' },
    { v: 'FALTA', l: 'Falta' },
    { v: 'CANCELADA', l: 'Cancelada' },
];

const MODALIDADE_OPCOES = [
    { v: 'PRESENCIAL', l: 'Presencial' },
    { v: 'ONLINE', l: 'Online' },
];

const estadoInicial = () => ({
    idPaciente: null,
    idPsicologo: null,
    dataHora: '',
    duracao: 50,
    statusSessao: 'AGENDADA',
    modalidadeSessao: 'PRESENCIAL',
    linkVideo: '',
    // Vazio = atendimento particular. Na edicao, deixar vazio limpa o convenio
    // da sessao (mesma semantica do idSlot).
    idPacientePlanoSaude: '',
});

const SectionTitle = ({ children, descricao }) => (
    <Box sx={{ mb: descricao ? 2 : 3 }}>
        <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
            {children}
        </Typography>
        {descricao && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1.5 }}>
                {descricao}
            </Typography>
        )}
    </Box>
);

export default function SessaoForm() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const isView = searchParams.get('view') === '1';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const soLeitura = isView || isVisualizador();

    const [tab, setTab] = useState(0);
    const [form, setForm] = useState(estadoInicial());
    const [psicologos, setPsicologos] = useState([]);
    const [carteirinhas, setCarteirinhas] = useState([]);
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [psicologoSelecionado, setPsicologoSelecionado] = useState(null);
    const [fetching, setFetching] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        (async () => {
            try {
                // Pacientes vêm do BuscaPaciente, sob demanda.
                const psi = await api.get('/psicologos');
                setPsicologos(Array.isArray(psi.data) ? psi.data : (psi.data?.content || []));
            } catch { /* ignore */ }
        })();
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/sessoes/${id}`);
                setForm({
                    idPaciente: data.idPaciente,
                    idPsicologo: data.idPsicologo,
                    dataHora: data.dataHora ? data.dataHora.substring(0, 16) : '',
                    duracao: data.duracao || 50,
                    statusSessao: data.statusSessao || 'AGENDADA',
                    modalidadeSessao: data.modalidadeSessao || 'PRESENCIAL',
                    linkVideo: data.linkVideo || '',
                    idPacientePlanoSaude: data.idPacientePlanoSaude || '',
                });
                setPacienteSelecionado({ id_paciente: data.idPaciente, nomeCompleto: data.nomePaciente });
                setPsicologoSelecionado({ id_psicologo: data.idPsicologo, nomeCompleto: data.nomePsicologo });
            } catch {
                setError('Nao foi possivel carregar a sessao.');
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit]);

    const setCampo = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));

    /**
     * Carteirinhas do paciente escolhido. So as ativas entram na lista: carteirinha
     * inativa ou de convenio inativo e recusada pelo servidor no agendamento.
     */
    useEffect(() => {
        if (!form.idPaciente) { setCarteirinhas([]); return; }
        api.get(`/pacientes/${form.idPaciente}/convenios`)
            .then(r => {
                const lista = Array.isArray(r.data) ? r.data : [];
                setCarteirinhas(lista.filter(c => c.ativo && c.planoSaudeAtivo));
            })
            .catch(() => setCarteirinhas([]));
    }, [form.idPaciente]);

    /** Carteirinha vencida na data da sessao: o servidor recusa, avisamos antes. */
    const carteirinhaEscolhida = carteirinhas.find(
        c => c.id_paciente_plano_saude === form.idPacientePlanoSaude);
    const carteirinhaVencidaNaData = Boolean(
        carteirinhaEscolhida?.validadeCarteirinha && form.dataHora
        && carteirinhaEscolhida.validadeCarteirinha < form.dataHora.substring(0, 10));

    const solicitarSalvar = async () => {
        setError('');
        if (!form.idPaciente) { setError('Selecione um paciente.'); return; }
        if (!form.dataHora) { setError('Data e hora obrigatorias.'); return; }
        if (!form.duracao || form.duracao <= 0) { setError('Duracao invalida.'); return; }

        const ok = await confirmAcao({
            title: 'Confirmar alterações',
            text: 'Deseja gravar os dados desta sessão?',
            confirmButtonText: 'Confirmar e salvar',
            cancelButtonText: 'Rever dados',
            icon: 'question',
        });
        if (ok) executarSalvar();
    };

    const executarSalvar = async () => {
        setSaving(true);
        try {
            const autorizacao = isEdit ? consumirAutorizacao(`sessao:${id}:editar`) : null;
            const payload = {
                ...form,
                autorizacao: autorizacao || null,
                dataHora: form.dataHora.length === 16 ? `${form.dataHora}:00` : form.dataHora,
                duracao: Number(form.duracao),
                linkVideo: form.modalidadeSessao === 'ONLINE' ? (form.linkVideo || null) : null,
                idSlot: null,
                // String vazia nao desserializa em Long: particular vai como null.
                idPacientePlanoSaude: form.idPacientePlanoSaude || null,
            };
            if (isEdit) {
                await api.put(`/sessoes/${id}`, payload);
                setSnackbar({ open: true, message: 'Sessao atualizada com sucesso!', severity: 'success' });
            } else {
                await api.post('/sessoes', payload);
                setSnackbar({ open: true, message: 'Sessao cadastrada com sucesso!', severity: 'success' });
            }
            setTimeout(() => navigate('/sessoes'), 1500);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data;
            setError(typeof msg === 'string' ? msg : 'Erro ao salvar. Verifique a ligacao com o servidor.');
        } finally {
            setSaving(false);
        }
    };

    const psicologoOptions = useMemo(() => psicologos.map(p => ({ id_psicologo: p.id_psicologo, nomeCompleto: p.nomeCompleto })), [psicologos]);

    if (fetching) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress sx={{ color: '#1ABB9C' }} />
        </Box>;
    }

    const SecaoDados = (
        <Box>
            <SectionTitle descricao="Paciente, psicologo, horario, modalidade e status.">Dados da Sessao</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <BuscaPaciente
                    label="Paciente *"
                    disabled={soLeitura}
                    value={pacienteSelecionado}
                    onChange={(v) => {
                        setPacienteSelecionado(v);
                        setForm(prev => ({
                            ...prev,
                            idPaciente: v?.id_paciente || null,
                            // Carteirinha e do paciente: trocar de paciente invalida a escolha.
                            idPacientePlanoSaude: '',
                        }));
                    }}
                />
                <Autocomplete
                    disabled={soLeitura}
                    options={psicologoOptions}
                    getOptionLabel={(o) => o?.nomeCompleto || ''}
                    isOptionEqualToValue={(o, v) => o.id_psicologo === v.id_psicologo}
                    value={psicologoSelecionado}
                    onChange={(_, v) => { setPsicologoSelecionado(v); setCampo('idPsicologo', v?.id_psicologo || null); }}
                    renderInput={(params) => <TextField {...params} label="Psicologo" size="small" />}
                />
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField fullWidth type="datetime-local" label="Data e Hora *" disabled={soLeitura}
                        InputLabelProps={{ shrink: true }}
                        value={form.dataHora}
                        onChange={(e) => setCampo('dataHora', e.target.value)} />
                    <TextField fullWidth type="number" label="Duracao (min) *" disabled={soLeitura}
                        value={form.duracao}
                        onChange={(e) => setCampo('duracao', e.target.value)}
                        inputProps={{ min: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField select fullWidth label="Modalidade" disabled={soLeitura}
                        value={form.modalidadeSessao}
                        onChange={(e) => setCampo('modalidadeSessao', e.target.value)}>
                        {MODALIDADE_OPCOES.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                    </TextField>
                    <TextField select fullWidth label="Status" disabled={soLeitura || isEdit}
                        helperText={isEdit && !soLeitura ? 'Status muda pelas ações da lista ou do calendário' : ''}
                        value={form.statusSessao}
                        onChange={(e) => setCampo('statusSessao', e.target.value)}>
                        {(isEdit ? STATUS_OPCOES : STATUS_OPCOES.filter(o => o.v === 'AGENDADA' || o.v === 'REALIZADA'))
                            .map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                    </TextField>
                </Box>
                <TextField select fullWidth label="Convenio" disabled={soLeitura || !form.idPaciente}
                    value={form.idPacientePlanoSaude}
                    onChange={(e) => setCampo('idPacientePlanoSaude', e.target.value)}
                    helperText={!form.idPaciente
                        ? 'Escolha o paciente para ver as carteirinhas.'
                        : carteirinhas.length === 0
                            ? 'Paciente sem carteirinha ativa: atendimento particular.'
                            : 'Particular = sem convenio. E desta carteirinha que a guia sai.'}>
                    <MenuItem value="">Particular</MenuItem>
                    {carteirinhas.map(c => (
                        <MenuItem key={c.id_paciente_plano_saude} value={c.id_paciente_plano_saude}>
                            {c.nomePlanoSaude} • {c.numeroCarteirinha}{c.padrao ? ' (padrao)' : ''}
                        </MenuItem>
                    ))}
                </TextField>
                {carteirinhaVencidaNaData && (
                    <Alert severity="warning">
                        A carteirinha venceu em {carteirinhaEscolhida.validadeCarteirinha} e nao cobre a data desta
                        sessao. O servidor vai recusar o agendamento por convenio.
                    </Alert>
                )}
            </Box>
        </Box>
    );

    const SecaoVideo = (
        <Box>
            <SectionTitle descricao={form.modalidadeSessao === 'ONLINE'
                ? "Link do Meet/video. Se vazio em sessao online, sera gerado automaticamente ao salvar."
                : "Disponivel apenas em sessoes online."}>Video</SectionTitle>
            {form.modalidadeSessao === 'ONLINE' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField fullWidth label="Link de Video" disabled={soLeitura}
                        value={form.linkVideo}
                        onChange={(e) => setCampo('linkVideo', e.target.value)} />
                    {form.linkVideo && (
                        <Stack direction="row" gap={1}>
                            <Button variant="outlined" startIcon={<VideocamIcon />}
                                href={form.linkVideo} target="_blank" rel="noopener"
                                sx={{ color: '#1ABB9C', borderColor: '#1ABB9C' }}>
                                Abrir reuniao
                            </Button>
                        </Stack>
                    )}
                </Box>
            ) : (
                <Typography color="text.secondary">Modalidade presencial — sem link de video.</Typography>
            )}
        </Box>
    );

    return (
        <Box sx={{ flexGrow: 1, pb: soLeitura ? 4 : 10, px: isMobile ? 1 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/sessoes')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                    {isView ? 'Visualizar Sessao' : isEdit ? 'Editar Sessao' : 'Nova Sessao'}
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}
                    variant={isMobile ? 'scrollable' : 'standard'} scrollButtons="auto"
                    sx={{
                        borderBottom: '1px solid #eee', bgcolor: '#fafafa',
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 56 },
                        '& .Mui-selected': { color: '#1ABB9C !important' },
                        '& .MuiTabs-indicator': { backgroundColor: '#1ABB9C', height: 3 },
                    }}>
                    <Tab icon={<EventAvailableIcon />} iconPosition="start" label="Dados" />
                    <Tab icon={<VideocamIcon />} iconPosition="start" label="Video" />
                </Tabs>

                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    {tab === 0 && SecaoDados}
                    {tab === 1 && SecaoVideo}
                </Box>
            </Paper>

            {!soLeitura && (
                <Paper elevation={3}
                    sx={{
                        position: 'fixed', bottom: 0, left: { xs: 0, md: 240 }, right: 0,
                        px: 3, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1,
                        bgcolor: '#fff', borderTop: '1px solid #eee', zIndex: 10,
                    }}>
                    <Button variant="outlined" onClick={() => navigate('/sessoes')} sx={{ color: '#666', borderColor: '#ccc' }}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={solicitarSalvar} disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: 4 }}>
                        {isEdit ? 'Atualizar Sessao' : 'Salvar Sessao'}
                    </Button>
                </Paper>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
